// /privat — Owner-only Privat-Tab (Doc 2026-05-17).
//
// Sektionen:
//   1. EVE-Greeting-Card mit Last-Seen-Gap-Awareness (warmer Vollgruß)
//   2. Heute-Block: Wirtschaftsnews (recycle TodayMacroBanner) + Markt-Outlook
//   3. Doc-Tools-Grid: Quick-Memory · System-Risk · Trade-Journal
//   4. End-of-Day-Ritual als CTA + Modal
//
// Backend: api/src/routes/eve-privat.ts.

import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  BrainCircuit,
  CheckCircle2,
  Loader2,
  Moon,
  RotateCcw,
  Save,
  Sun,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { TodayMacroBanner } from '@/components/markets/today-macro-banner';
import { useEveMemory } from '@/hooks/use-eve-memory';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import { selectAccessToken, selectProfile, useAuthStore } from '@/stores/auth-store';
import { useEvePageContextStore } from '@/stores/eve-page-context-store';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import {
  berlinDateKey,
  isBerlinWeekend,
  berlinHour,
  formatBerlinTime,
  formatBerlinWeekdayDate,
  formatBerlinRelative,
  formatGap,
} from '@/lib/berlin-time';
import {
  listTradeJournal,
  pingLastSeen,
  submitEndOfDay,
  type DayMood,
  type LastSeenResponse,
  type TradeJournalEntry,
} from '@/lib/privat-api';

export function PrivatPage() {
  const accessToken = useAuthStore(selectAccessToken);
  const profile = useAuthStore(selectProfile);
  const fullData = useMarketStore(selectFullData);
  const { addMemory } = useEveMemory();
  const setPageContext = useEvePageContextStore((s) => s.setPageContext);
  const clearPageContext = useEvePageContextStore((s) => s.clearPageContext);
  const askEve = useEveDrawerStore((s) => s.askEve);

  const [lastSeen, setLastSeen] = useState<LastSeenResponse | null>(null);
  const [journal, setJournal] = useState<ReadonlyArray<TradeJournalEntry>>([]);
  const [eodOpen, setEodOpen] = useState(false);

  // Last-Seen + Journal beim Mount holen.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    void pingLastSeen(accessToken).then((r) => {
      if (!cancelled) setLastSeen(r);
    });
    void listTradeJournal(accessToken, 10).then((r) => {
      if (!cancelled) setJournal(r.entries);
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Page-Context damit EVE weiß wo Doc ist + Greeting-Daten kennt.
  useEffect(() => {
    setPageContext({
      pageKey: '/privat',
      title: 'Privat · Doc-only',
      summary:
        'Owner-only Privat-Tab. EVE mit voller Persona, ohne Disclaimer. Last-Seen-Awareness aktiv.',
      data: { lastSeen },
    });
    return () => clearPageContext();
  }, [lastSeen, setPageContext, clearPageContext]);

  const reloadJournal = () => {
    if (!accessToken) return;
    void listTradeJournal(accessToken, 10).then((r) => setJournal(r.entries));
  };

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mx-auto max-w-5xl space-y-4">
        <PrivatHeader lastSeen={lastSeen} nickname={profile?.nickname ?? 'Doc'} />

        <EveGreetingCard
          lastSeen={lastSeen}
          nickname={profile?.nickname ?? 'Doc'}
          onAskEveForBriefing={() =>
            askEve(
              'Gib mir die Kurz-Lage: Markt-Outlook heute, größte Risiken, was ich auf dem Radar haben sollte. Persönlich, knapp, in vollen Sätzen.',
              { displayText: 'Gib mir die Kurz-Lage' },
            )
          }
        />

        {/* Heute-Block: Datum + Wirtschaftsnews + Markt-Outlook */}
        <section className="glass-strong rounded-2xl p-4">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <h2 className="text-[12px] font-bold uppercase tracking-wider text-amber-300">
              Heute steht an
            </h2>
            <TodayContextLine />
          </div>
          <div className="mb-3">
            <TodayMacroBanner variant="rail" highOnly={false} limit={6} />
          </div>
          <MarketOutlookRow fullData={fullData} />
        </section>

        {/* Quick-Memory bleibt — ist ein echtes Doc-Tool für unterwegs. */}
        <section>
          <h2 className="mb-2 px-1 text-[12px] font-bold uppercase tracking-wider text-neutral-400">
            Quick-Notiz
          </h2>
          <QuickMemoryCard onSave={(fact) => addMemory(fact)} />
        </section>

        {/* End-of-Day-Ritual CTA */}
        <EndOfDayCTA
          journal={journal}
          onOpen={() => setEodOpen(true)}
        />
      </div>

      {/* End-of-Day-Modal */}
      <AnimatePresence>
        {eodOpen ? (
          <EndOfDayModal
            onClose={() => setEodOpen(false)}
            onSubmit={async (input) => {
              if (!accessToken) return;
              await submitEndOfDay(accessToken, input);
              reloadJournal();
              setEodOpen(false);
            }}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────────────────────

function PrivatHeader({
  lastSeen,
  nickname,
}: {
  lastSeen: LastSeenResponse | null;
  nickname: string;
}) {
  const lastSeenLabel = formatLastSeenShort(lastSeen?.prev_at ?? null);
  return (
    <header className="flex flex-wrap items-end justify-between gap-3 border-b border-white/5 pb-3">
      <div>
        <div className="flex items-center gap-2">
          <Moon className="size-5 text-amber-300" />
          <h1 className="text-xl font-bold">Privat</h1>
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
            Owner-only
          </span>
        </div>
        <p className="mt-0.5 text-[12px] text-neutral-500">
          {nickname}, EVE weiß was war. Und was heute ansteht.
        </p>
      </div>
      <div className="text-right text-[12px]">
        <div className="text-neutral-500">Letztes Mal hier</div>
        <div className="font-medium tabular text-neutral-200">{lastSeenLabel}</div>
      </div>
    </header>
  );
}

// ── EVE-Greeting-Card ───────────────────────────────────────────────────────

function EveGreetingCard({
  lastSeen,
  nickname,
  onAskEveForBriefing,
}: {
  lastSeen: LastSeenResponse | null;
  nickname: string;
  onAskEveForBriefing: () => void;
}) {
  const greeting = useMemo(() => buildGreeting(lastSeen, nickname), [lastSeen, nickname]);
  // Wenn lastSeen noch nicht geladen oder Gap zu klein für eine Greeting-Card.
  if (!greeting) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.06] via-neutral-950/70 to-neutral-950/40 p-5"
    >
      <div className="flex items-start gap-4">
        <div className="grid size-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-base font-bold text-neutral-950">
          E
        </div>
        <div className="flex-1 space-y-3">
          <p className="text-[14px] leading-relaxed text-neutral-100">{greeting.text}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onAskEveForBriefing}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25"
            >
              Ja, gib mir das Update
            </button>
            <span className="self-center text-[12px] text-neutral-500">oder einfach reden.</span>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

interface Greeting {
  text: string;
}

function buildGreeting(lastSeen: LastSeenResponse | null, nickname: string): Greeting | null {
  if (!lastSeen) return null;
  const name = nickname || 'Doc';
  const prev = lastSeen.prev_at ? new Date(lastSeen.prev_at) : null;
  const now = new Date();
  const todayBerlin = berlinDateKey(now);
  const prevBerlin = prev ? berlinDateKey(prev) : null;
  const sameDay = prevBerlin === todayBerlin;
  const gapH = prev ? (now.getTime() - prev.getTime()) / 3_600_000 : null;
  const hour = berlinHour(now);
  const isMorning = hour >= 5 && hour < 11;
  const isEvening = hour >= 18;
  const weekdayDate = formatBerlinWeekdayDate(now);
  const isWeekend = isBerlinWeekend(now);

  // Erster Besuch im Privat-Modus — warm, mit Substanz, kein generisches Hi.
  if (!prev) {
    const tagPhase = isWeekend
      ? 'Wochenende — Märkte zu, Kopf frei. Gute Zeit zum Aufräumen.'
      : isMorning
        ? 'Morgens. Asien hat schon gehandelt, Europa fängt gleich an.'
        : isEvening
          ? 'Abend. US-Session ist hinter dir, oder du hängst noch dran.'
          : 'Mittendrin im Tag.';
    return {
      text:
        `Hey ${name}. Willkommen im Privat-Modus — hier sind wir unter uns, ohne Disclaimer und ohne Briefing-Format. ` +
        `Heute ist ${weekdayDate}. ${tagPhase} ` +
        `Du findest unten was heute ansteht, kannst kurz Notizen einwerfen, oder wenn der Tag rum ist mit dem Ritual abschließen. ` +
        `Wenn du was los werden willst — egal ob Markt, Code oder einfach reden — frag einfach.`,
    };
  }

  // Kurzer Gap am selben Tag — keine Karte, nur die Header-Last-Seen-Anzeige.
  if (sameDay && gapH !== null && gapH < 2) {
    return null;
  }

  // Vollgruß für Tageswechsel ODER langer Gap.
  if (!sameDay) {
    const prevTime = formatBerlinTime(prev);
    if (isMorning) {
      return {
        text:
          `Guten Morgen, ${name}. Letztes Mal warst du ${formatBerlinRelative(prev)} um ${prevTime}. ` +
          `Heute ist ${weekdayDate}. Ich hoffe du hast geschlafen statt durchcodet — du schiebst sonst zwölf Stunden am Stück. ` +
          `Schau dir den Heute-Block unten an oder lass mich kurz die Lage zusammenfassen.`,
      };
    }
    return {
      text:
        `Hey ${name}, schön dass du wieder da bist. Du warst ${formatBerlinRelative(prev)} um ${prevTime} noch hier — ` +
        `${formatGap(gapH)} weg. Heute ist ${weekdayDate}. ` +
        `Wenn du willst, gehe ich dir kurz durch was ansteht. Sonst erzähl einfach, wie es dir geht.`,
    };
  }

  // Selber Tag aber > 2h weg.
  if (gapH !== null && gapH >= 2) {
    const prevTime = formatBerlinTime(prev);
    if (isEvening) {
      return {
        text: `Da bist du wieder, ${name}. Letztes Mal um ${prevTime} — alles okay? Wenn du noch was los werden willst bevor der Tag rum ist, ich höre zu.`,
      };
    }
    return {
      text: `Wieder da, ${name}. Du warst seit ${prevTime} weg. Wenn was war, sag's. Ansonsten — was machen wir jetzt?`,
    };
  }

  return null;
}

// ── Today-Context-Line (Datum · Wochentag · Marktphase) ────────────────────

function TodayContextLine() {
  // useState-trick damit die Anzeige jede Minute frisch ist (Marktphase wechselt).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const weekday = formatBerlinWeekdayDate(now);
  const time = formatBerlinTime(now);
  const phase = berlinMarketPhase(now);
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-neutral-400">
      <span className="font-medium text-neutral-200">{weekday}</span>
      <span className="text-neutral-600">·</span>
      <span className="tabular">{time}</span>
      <span className="text-neutral-600">·</span>
      <span className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-medium text-amber-300">
        {phase}
      </span>
    </div>
  );
}

function berlinMarketPhase(d: Date): string {
  if (isBerlinWeekend(d)) return 'Wochenende · Aktien zu, Crypto läuft';
  const h = berlinHour(d);
  if (h < 8) return 'Vorbörse · Asien läuft';
  if (h < 9) return 'Europa-Open gleich';
  if (h < 15) return 'Europa-Handel';
  if (h < 15.5) return 'US-Open gleich';
  if (h < 22) return 'US-Handel';
  return 'After-Hours · Asien wacht auf';
}

// ── Markt-Outlook ───────────────────────────────────────────────────────────

interface FullDataLite {
  prices?: {
    bitcoin?: { usd?: number | null; usd_24h_change?: number | null } | null;
    ethereum?: { usd?: number | null; usd_24h_change?: number | null } | null;
  } | null;
  macro?: {
    vix?: { price?: number | null } | null;
    spx?: { price?: number | null } | null;
    dxy?: { price?: number | null } | null;
    us10y?: { price?: number | null } | null;
  } | null;
}

function MarketOutlookRow({ fullData }: { fullData: FullDataLite | null }) {
  const items: Array<{ label: string; value: string; change?: number | null }> = [
    {
      label: 'BTC',
      value: fmtPrice(fullData?.prices?.bitcoin?.usd),
      change: fullData?.prices?.bitcoin?.usd_24h_change ?? null,
    },
    {
      label: 'ETH',
      value: fmtPrice(fullData?.prices?.ethereum?.usd),
      change: fullData?.prices?.ethereum?.usd_24h_change ?? null,
    },
    { label: 'VIX', value: fmtPrice(fullData?.macro?.vix?.price) },
    { label: 'SPX', value: fmtPrice(fullData?.macro?.spx?.price) },
    { label: 'DXY', value: fmtPrice(fullData?.macro?.dxy?.price) },
    { label: 'US10Y', value: fmtPrice(fullData?.macro?.us10y?.price) },
  ];
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-500">
        Markt-Outlook · Live
      </div>
      <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
        {items.map((it) => {
          const changeTone =
            it.change != null && Number.isFinite(it.change)
              ? it.change >= 0
                ? 'text-emerald-400'
                : 'text-rose-400'
              : 'text-neutral-500';
          const changeText =
            it.change != null && Number.isFinite(it.change)
              ? `${it.change >= 0 ? '+' : ''}${it.change.toFixed(1)}%`
              : null;
          return (
            <div
              key={it.label}
              className="rounded-lg border border-white/5 bg-white/[0.02] px-2 py-1.5"
            >
              <div className="text-[10px] text-neutral-500">{it.label}</div>
              <div className="font-medium tabular text-[13px] text-neutral-100">{it.value}</div>
              {changeText ? (
                <div className={`text-[10px] tabular ${changeTone}`}>{changeText}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Quick-Memory-Card ───────────────────────────────────────────────────────

function QuickMemoryCard({ onSave }: { onSave: (fact: string) => Promise<unknown> }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    const fact = text.trim();
    if (!fact) return;
    setSaving(true);
    try {
      await onSave(fact);
      setText('');
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="glass rounded-2xl p-4">
      <div className="mb-2 flex items-center gap-2">
        <BrainCircuit className="size-4 text-amber-300" />
        <h3 className="text-[12px] font-bold uppercase tracking-wider text-neutral-300">
          Quick-Memory
        </h3>
      </div>
      <p className="mb-2 text-[11px] text-neutral-500">
        Ein-Klick-Notiz für EVE — wird im Memory-Index aufgenommen.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="z.B. „Mario hat heute angefragt, Briefing-Mail bis Freitag."
        rows={3}
        className="mb-2 w-full resize-none rounded-md border border-white/10 bg-neutral-900/60 px-2 py-1.5 text-[12px] text-neutral-100 focus:border-amber-500/50 focus:outline-none"
      />
      <button
        type="button"
        onClick={() => void save()}
        disabled={saving || text.trim().length === 0}
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[12px] font-semibold text-amber-200 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {saving ? (
          <Loader2 className="size-3 animate-spin" />
        ) : saved ? (
          <CheckCircle2 className="size-3" />
        ) : (
          <Save className="size-3" />
        )}
        {saving ? 'Speichere…' : saved ? 'Gespeichert' : 'Save Memory'}
      </button>
    </div>
  );
}

// ── End-of-Day-Ritual ───────────────────────────────────────────────────────

function EndOfDayCTA({
  journal,
  onOpen,
}: {
  journal: ReadonlyArray<TradeJournalEntry>;
  onOpen: () => void;
}) {
  // Letzten EOD-Eintrag (symbol = 'EOD') als „letzte Reflexion".
  const lastEod = journal.find((e) => e.symbol === 'EOD');
  const lastLabel = lastEod ? formatJournalDate(lastEod.created_at) : 'noch nie';

  return (
    <section className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Moon className="mt-0.5 size-5 text-amber-300" />
          <div>
            <h3 className="text-[13px] font-bold text-amber-200">Tag schließen</h3>
            <p className="text-[12px] text-neutral-400">
              Drei Fragen: was lief gut, was war Mist, was bleibt offen.
              <span className="ml-1 text-neutral-600">· Letztes Mal: {lastLabel}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25"
        >
          <RotateCcw className="size-3" />
          Ritual starten
        </button>
      </div>
    </section>
  );
}

function EndOfDayModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (input: {
    what_worked?: string;
    what_didnt?: string;
    what_open?: string;
    mood?: DayMood;
  }) => Promise<void>;
}) {
  const [worked, setWorked] = useState('');
  const [didnt, setDidnt] = useState('');
  const [open, setOpen] = useState('');
  const [mood, setMood] = useState<DayMood | ''>('');
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!worked && !didnt && !open) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        what_worked: worked.trim() || undefined,
        what_didnt: didnt.trim() || undefined,
        what_open: open.trim() || undefined,
        mood: mood || undefined,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 240, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong w-[min(560px,92vw)] rounded-2xl border border-amber-500/30 p-5"
      >
        <div className="mb-3 flex items-center gap-2">
          <Moon className="size-5 text-amber-300" />
          <h2 className="text-lg font-bold text-amber-200">Tag schließen</h2>
        </div>
        <p className="mb-4 text-[12px] text-neutral-400">
          Drei Sätze reichen. Was wichtig ist, landet als Memory + im Journal.
        </p>

        <div className="space-y-3">
          <Question
            icon={<TrendingUp className="size-4 text-emerald-400" />}
            label="Was lief gut?"
            value={worked}
            onChange={setWorked}
          />
          <Question
            icon={<TrendingDown className="size-4 text-rose-400" />}
            label="Was war Mist?"
            value={didnt}
            onChange={setDidnt}
          />
          <Question
            icon={<AlertCircle className="size-4 text-amber-400" />}
            label="Was bleibt offen?"
            value={open}
            onChange={setOpen}
          />

          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-neutral-500">
              Wie war der Tag insgesamt?
            </label>
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  { k: 'focused', l: 'Fokussiert' },
                  { k: 'satisfied', l: 'Zufrieden' },
                  { k: 'calm', l: 'Ruhig' },
                  { k: 'exhausted', l: 'Erschöpft' },
                  { k: 'tilted', l: 'Tilted' },
                ] as const
              ).map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setMood(mood === m.k ? '' : m.k)}
                  className={`rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
                    mood === m.k
                      ? 'border-amber-500/50 bg-amber-500/20 text-amber-200'
                      : 'border-white/10 bg-white/[0.02] text-neutral-400 hover:border-amber-500/30 hover:text-amber-300'
                  }`}
                >
                  {m.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-[12px] text-neutral-400 hover:text-neutral-200"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/20 px-3 py-1.5 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/30 disabled:opacity-40"
          >
            {saving ? <Loader2 className="size-3 animate-spin" /> : <Sun className="size-3" />}
            Tag abschließen
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Question({
  icon,
  label,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-neutral-500">
        {icon}
        {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="w-full resize-none rounded-md border border-white/10 bg-neutral-900/60 px-2 py-1.5 text-[12px] text-neutral-100 focus:border-amber-500/50 focus:outline-none"
      />
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatLastSeenShort(iso: string | null): string {
  if (!iso) return 'erste Sitzung';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return `${formatBerlinRelative(d)} · ${formatBerlinTime(d)}`;
}

function formatJournalDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function fmtPrice(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}
